import { useState } from 'react';
import { Loader2, ShieldAlert, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function FinalizeSequenceDiagnosticPanel() {
  const [lookupId, setLookupId] = useState('9716021');
  const [entityId, setEntityId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runDiagnostic = async () => {
    setLoading(true);
    setResult(null);
    try {
      const params = {};
      if (lookupId.trim()) params.lookup_id = lookupId.trim();
      if (entityId) params.entity_id = entityId;
      if (!params.lookup_id && !params.entity_id) {
        toast.error('Provide a booking/invoice ID or select an entity.');
        setLoading(false);
        return;
      }
      const { data } = await diagnosticsAPI.finalizeSequence(params);
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-orange-500" />
          Finalize Sequence Diagnostic
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Read-only: identifies what finalized invoice (or account setting) is blocking
          a draft from being finalized. Fetches ALL finalized invoices from Pennylane
          for the entity and finds the latest by issue date.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Controls */}
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Uplisting ID or Pennylane Invoice ID</label>
            <input
              type="text"
              className="block mt-1 border rounded px-2 py-1.5 text-sm bg-background w-36"
              value={lookupId}
              onChange={(e) => setLookupId(e.target.value)}
              placeholder="e.g. 9716021"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Entity (optional, auto-resolved)</label>
            <select
              className="block mt-1 border rounded px-2 py-1.5 text-sm bg-background"
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
            >
              <option value="">Auto-detect from invoice</option>
              <option value="bi_immo">Bonjour Immobilier (bi_immo)</option>
              <option value="afpv">AFPV Invest (afpv)</option>
            </select>
          </div>
          <Button onClick={runDiagnostic} disabled={loading} size="sm">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Run Diagnostic
          </Button>
        </div>

        {/* Results */}
        {result && (
          <div className="space-y-4 mt-4">
            {/* Build commit + resolution */}
            {result.build_commit && (
              <div className="text-xs text-muted-foreground space-y-0.5">
                <div>
                  BUILD_COMMIT: <code className="font-mono bg-muted px-1 rounded">{result.build_commit}</code>
                  {' | '}Checked at: {result.checked_at || '-'}
                  {result.entity_id && <>{' | '}Entity: <code className="font-mono bg-muted px-1 rounded">{result.entity_id}</code></>}
                </div>
                {result.resolution_method && (
                  <div>
                    Resolved via: <code className="font-mono bg-muted px-1 rounded">{result.resolution_method}</code>
                    {result.pennylane_invoice_id && (
                      <>{' | '}Pennylane ID: <code className="font-mono bg-muted px-1 rounded">{result.pennylane_invoice_id}</code></>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Top-level error */}
            {result.error && (
              <ErrorBlock message={result.error} />
            )}

            {/* Account Identity */}
            {result.account_identity && (
              <Section title="Account Identity">
                {result.account_identity.error ? (
                  <ErrorBlock message={result.account_identity.error} />
                ) : (
                  <KVTable data={result.account_identity} />
                )}
              </Section>
            )}

            {/* Target Invoice */}
            {result.target_invoice ? (
              <Section title="Target Invoice">
                <div className="space-y-2">
                  <KVTable data={{
                    'ID': result.target_invoice.id,
                    'Date': result.target_invoice.date,
                    'Invoice #': result.target_invoice.invoice_number || '(draft, no number)',
                    'Status': result.target_invoice.draft ? 'Draft' : 'Finalized',
                    'Amount': `${result.target_invoice.amount} ${result.target_invoice.currency || ''}`,
                    'Customer': result.target_invoice.customer_name || '-',
                    'External Ref': result.target_invoice.external_reference || '-',
                    'Is Uplisting': result.target_invoice.is_uplisting ? 'Yes' : 'No',
                  }} />
                </div>
              </Section>
            ) : result.target_invoice_error ? (
              <Section title="Target Invoice">
                <ErrorBlock message={result.target_invoice_error} />
              </Section>
            ) : null}

            {/* Blocking Diagnosis */}
            {result.blocking_diagnosis && (
              <Section title="Blocking Diagnosis">
                {result.blocking_diagnosis.blocked === true ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400 font-medium text-sm">
                      <AlertTriangle className="h-4 w-4" />
                      BLOCKED
                    </div>
                    <p className="text-sm">{result.blocking_diagnosis.explanation}</p>
                    <div className="border border-orange-300 bg-orange-50 dark:bg-orange-950/30 rounded p-3 text-sm space-y-1">
                      <p className="font-medium">Blocker type: <Badge variant="outline">{result.blocking_diagnosis.blocker_type}</Badge></p>
                      <p>{result.blocking_diagnosis.recommendation}</p>
                    </div>
                  </div>
                ) : result.blocking_diagnosis.blocked === false ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-green-600 dark:text-green-400 font-medium text-sm">
                      <CheckCircle2 className="h-4 w-4" />
                      NOT BLOCKED by date sequence
                    </div>
                    <p className="text-sm">{result.blocking_diagnosis.explanation}</p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{result.blocking_diagnosis.explanation}</p>
                )}
              </Section>
            )}

            {/* Latest Finalized Invoice (highlighted) */}
            {result.latest_finalized && (
              <Section title="Latest Finalized Invoice (the sequence baseline)">
                <div className="border-2 border-orange-400 bg-orange-50 dark:bg-orange-950/20 rounded p-3">
                  <KVTable data={{
                    'ID': result.latest_finalized.id,
                    'Invoice #': result.latest_finalized.invoice_number,
                    'Date': result.latest_finalized.date,
                    'Amount': `${result.latest_finalized.amount} ${result.latest_finalized.currency || ''}`,
                    'Customer': result.latest_finalized.customer_name || '-',
                    'External Ref': result.latest_finalized.external_reference || '-',
                    'Is Uplisting Booking': result.latest_finalized.is_uplisting ? 'Yes' : 'No',
                  }} />
                </div>
              </Section>
            )}
            {result.latest_finalized === null && !result.error && (
              <Section title="Latest Finalized Invoice">
                <p className="text-sm text-muted-foreground italic">
                  No finalized invoices found in this entity. The 422 block may come
                  from an account-level setting (numbering baseline, fiscal year start).
                  Check the Account Settings section below.
                </p>
              </Section>
            )}

            {/* Summary counts */}
            {result.total_invoices != null && (
              <Section title="Invoice Counts">
                <KVTable data={{
                  'Total invoices in entity': result.total_invoices,
                  'Finalized': result.finalized_count,
                  'Drafts': result.draft_count,
                  'Drafts blocked by sequence': result.drafts_blocked_count ?? '-',
                }} />
              </Section>
            )}

            {/* Blocked drafts */}
            {result.drafts_blocked_by_sequence?.length > 0 && (
              <Section title={`Drafts Blocked by Sequence (${result.drafts_blocked_count})`}>
                <p className="text-xs text-muted-foreground mb-2">
                  These drafts have issue dates before the latest finalized invoice
                  and cannot be finalized until the sequence is resolved.
                </p>
                <InvoiceTable invoices={result.drafts_blocked_by_sequence} />
              </Section>
            )}

            {/* All finalized invoices */}
            {result.finalized_invoices?.length > 0 && (
              <Section title={`All Finalized Invoices (${result.finalized_count})`}>
                <InvoiceTable
                  invoices={result.finalized_invoices}
                  highlightId={result.latest_finalized?.id}
                />
              </Section>
            )}

            {/* Account Settings */}
            {result.account_settings_probes && (
              <Section title="Account Settings Probes">
                {Object.entries(result.account_settings_probes).map(([label, data]) => (
                  <div key={label} className="mb-3">
                    <p className="text-xs font-semibold text-muted-foreground mb-1">{label}</p>
                    {data.error ? (
                      <ErrorBlock message={data.error} />
                    ) : (
                      <div className="text-xs space-y-1">
                        <KVTable data={flattenForDisplay(data)} />
                      </div>
                    )}
                  </div>
                ))}
                <div className="border-l-2 border-muted-foreground/30 pl-2 mt-2">
                  <p className="text-xs text-muted-foreground">
                    <Info className="inline h-3 w-3 mr-1" />
                    If these probes return 404 or no fiscal/numbering fields, the V2 API
                    does not expose the setting. Check it directly in the Pennylane UI
                    under Settings &gt; Invoicing / Numbering.
                  </p>
                </div>
              </Section>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Section({ title, children }) {
  return (
    <div className="border rounded-lg p-3">
      <h3 className="text-sm font-semibold mb-2">{title}</h3>
      {children}
    </div>
  );
}

function KVTable({ data }) {
  return (
    <table className="text-xs w-full">
      <tbody>
        {Object.entries(data).map(([k, v]) => (
          <tr key={k} className="border-t border-muted">
            <td className="px-2 py-1 text-muted-foreground whitespace-nowrap">{k}</td>
            <td className="px-2 py-1 font-mono break-all">
              {v == null ? '-' : typeof v === 'object' ? JSON.stringify(v) : String(v)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ErrorBlock({ message }) {
  return (
    <div className="border border-red-300 bg-red-50 dark:bg-red-950/30 rounded p-2 text-xs text-red-700 dark:text-red-400">
      <AlertTriangle className="inline h-3 w-3 mr-1" />
      {message}
    </div>
  );
}

function InvoiceTable({ invoices, highlightId }) {
  return (
    <div className="overflow-x-auto">
      <table className="text-xs w-full border-collapse">
        <thead>
          <tr className="bg-muted/50">
            <th className="px-2 py-1 text-left">Invoice #</th>
            <th className="px-2 py-1 text-left">Date</th>
            <th className="px-2 py-1 text-left">Customer</th>
            <th className="px-2 py-1 text-left">Ext Ref</th>
            <th className="px-2 py-1 text-left">Type</th>
            <th className="px-2 py-1 text-right">Amount</th>
            <th className="px-2 py-1 text-left">ID</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv) => {
            const isHighlighted = highlightId && String(inv.id) === String(highlightId);
            return (
              <tr
                key={inv.id}
                className={
                  isHighlighted
                    ? 'border-t border-orange-300 bg-orange-100 dark:bg-orange-950/40 font-medium'
                    : 'border-t border-muted'
                }
              >
                <td className="px-2 py-1 font-mono">{inv.invoice_number || '(draft)'}</td>
                <td className="px-2 py-1">{inv.date || '-'}</td>
                <td className="px-2 py-1">{inv.customer_name || '-'}</td>
                <td className="px-2 py-1 max-w-[200px] truncate">{inv.external_reference || '-'}</td>
                <td className="px-2 py-1">
                  {inv.is_uplisting ? (
                    <Badge variant="outline" className="text-[10px]">uplisting</Badge>
                  ) : (
                    <Badge className="bg-purple-600 text-[10px]">other</Badge>
                  )}
                </td>
                <td className="px-2 py-1 text-right font-mono">{inv.amount}</td>
                <td className="px-2 py-1 font-mono text-muted-foreground">{inv.id}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Flatten a settings probe result for display, skipping the http_status and keys array. */
function flattenForDisplay(data) {
  const out = {};
  for (const [k, v] of Object.entries(data)) {
    if (k === 'keys') {
      out['Available keys'] = Array.isArray(v) ? v.join(', ') : String(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}
